import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import { WhitelabelOrganizationIndex } from '../api/types';
import config from '../config';

async function getWhiteLabelOrganization(props: {
  axios: AxiosInstance;
  searchString: string;
  itemsPerPage: number;
  page?: number;
}): Promise<WhitelabelOrganizationIndex[]> {
  const { axios, searchString, itemsPerPage, page } = props;

  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/whitelabel-organizations`, {
    params: {
      searchString: searchString || '',
      itemsPerPage,
      page,
    },
  });
  return data;
}

export default function useWhiteLabelOrganizationsQuery({
  searchString,
  itemsPerPage,
  page,
}: {
  searchString: string;
  itemsPerPage: number;
  page?: number;
}) {
  const axios = useAxios();
  const toast = useToast();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey({ searchString, itemsPerPage, page }),
    queryFn: () => getWhiteLabelOrganization({ axios, searchString, itemsPerPage, page }),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, query.error as any);
    }
  }, [query.error, query.isError, toast]);

  return query;
}

function getKey({ searchString, itemsPerPage, page }: { searchString: string; itemsPerPage: number; page?: number }) {
  return ['Whitelabel Organization', searchString, itemsPerPage, page] as const satisfies QueryKey;
}

useWhiteLabelOrganizationsQuery.getKey = getKey;
